// src/game/frameLoop.ts — Kare saati (ARCHITECTURE §3 kural 1–3; WP9): accumulator + dt snap + tavan sıfırlama + slow-mo (timeScale
// yalnız acc'ye) + uyarlanabilir interpolasyon kararı (ilk RATE_SKIP kare atlanır, RATE_SAMPLES karenin rAF aralığı medyanı).
// three'siz ve DOM'suz: game.ts çağırır, test/frameLoop.test.ts Node'da doğrular. Tahsis yalnız ölçüm sırasında (örnek dizisi ≤ 98 sayı);
// kare başına sıfır. game.ts'ten ayrıldı çünkü game.ts three + Vite `?url` importu taşır (Node'da yüklenemez).
import { TICK_DT } from '../config/constants.ts';
import { T } from '../config/tuning.ts';

/** dt snap eşiği: |dtReal − TICK_DT| < 2 ms → dtReal = TICK_DT (rAF 16.2–17.3 ms titremesi 0/2-tick kare üretmesin). */
export const SNAP_EPS = 0.002;
/** Bir karede en fazla 4 tick (spiral of death yok); tavana çarpınca artık atılır. */
export const MAX_STEPS = 4;
/** Sekme arka planı: dt tavanı 0.25 s. */
export const DT_CAP = 0.25;
/** rAF aralığı ölçümü: ilk RATE_SKIP kare atlanır, RATE_SAMPLES karenin medyanı; |medyan − 16.7| ≤ RATE_TOL ms → 60 Hz kabulü. */
export const RATE_SKIP = 8;
export const RATE_SAMPLES = 90;
export const RATE_TOL = 0.9;
const RAF_60_MS = 1000 / 60;

export interface FrameClock {
  /** Biriken sim zamanı (sn). */
  acc: number;
  /** 60 Hz ekran kabulü → dt snap açık (varsayılan true; 90 kare sonra karar). */
  snap60: boolean;
  /** Render interpolasyonu (120/144 Hz'de açık). */
  interpolate: boolean;
  /** Ölçüm sürüyor mu (ilk açılış; resize/görünürlükte yeniden). */
  measuring: boolean;
  /** Son ölçülen rAF aralığı medyanı (ms; 0 = henüz yok). */
  rafMedianMs: number;
  /** Ölçüm örnekleri (ms); yalnız `measuring` iken büyür, karar sonrası boşaltılır. */
  readonly samples: number[];
}
export function createFrameClock(): FrameClock {
  return { acc: 0, snap60: true, interpolate: false, measuring: true, rafMedianMs: 0, samples: [] };
}

/** Ham rAF farkı (sn) → tavan (DT_CAP) + dt snap (kural 1). Negatif / NaN (ilk kare) → TICK_DT. */
export function clampDt(clock: FrameClock, dtRaw: number): number {
  if (!(dtRaw >= 0)) return TICK_DT;
  const dt = Math.min(dtRaw, DT_CAP);
  return clock.snap60 && Math.abs(dt - TICK_DT) < SNAP_EPS ? TICK_DT : dt;
}

/**
 * Bir karede koşulacak tick sayısı. `acc += dt·timeScale` (duraklamada birikmez; slow-mo yalnız burada).
 *
 * İKİ KİP:
 *  • ÇEVRİMDIŞI (varsayılan): tavan `MAX_STEPS` = 4 ve tavana çarpınca **artık ATILIR** (kural 2, "ileri sarma" yok).
 *    Sekme dönüşünde 0.18 s'lik birikim 2-3 kare 4×-hızlı sarma yapıyordu; oyuncu tek başına olduğu için zamanı
 *    düşürmek doğru davranış.
 *  • YETİŞME (`catchUp = true`, ağ maçı): tavan `T.NET.catchUpMaxSteps` ve artık **KORUNUR**. Sim saati sunucunun
 *    tick akışına kilitli (NETWORK.md değişmez 12); zamanı düşürmek istemciyi kalıcı olarak geride bırakır ve bir
 *    daha yetişemez (proposals/multiplayer.md §4.5). Fazla tick sonraki karelere taşınır.
 *    Not: `DT_CAP` (0.25 s) hâlâ geçerli — çok uzun bir donmadan sonra doğru cevap "10 saniyeyi simüle et" değil,
 *    "sunucudan tam durum iste"tir (`T.NET.resyncBehindTicks`).
 */
export function stepAccumulator(clock: FrameClock, dt: number, timeScale: number, paused: boolean, catchUp = false): number {
  if (!paused) clock.acc += dt * timeScale;
  const cap = catchUp ? T.NET.catchUpMaxSteps : MAX_STEPS;
  // EŞİK: yetişmede `acc >= TICK_DT` yerine bir nanosaniyelik pay bırakılır. Tekrarlanan çıkarma `acc`ye ufak bir
  // kayan nokta artığı bırakıyor (60 tick sonra 0.016666666666665 — TICK_DT'nin AZ ALTINDA) ve o tick bir kare
  // gecikiyor. Çevrimdışında görünmez (artık zaten atılıyor), ağda sim saati sunucuya kilitli olduğu için her tick
  // sayılır. Çevrimdışı kip DOKUNULMADI: eşik yalnız yetişmede uygulanıyor, bugünkü his birebir aynı kalsın.
  const need = catchUp ? TICK_DT - PENDING_EPS : TICK_DT;
  let steps = 0;
  while (clock.acc >= need && steps < cap) { clock.acc -= TICK_DT; steps++; }
  if (steps === cap && !catchUp) clock.acc = 0;
  else if (catchUp && clock.acc < PENDING_EPS) clock.acc = 0;   // artık negatife kaymasın
  return steps;
}

/** Kayan nokta payı (sn): `acc` tekrarlanan çıkarmalarla ufak bir artık taşır (60 tick'lik birikimden sonra
 *  ~1e-16·60). 1 ns eşik gerçek bir gecikmeyi asla gizlemez; yalnız yetişme kipinde kullanılır. */
const PENDING_EPS = 1e-9;
export const pendingTicks = (clock: FrameClock): number => Math.floor(clock.acc / TICK_DT + PENDING_EPS);

/* ─────────────────────────────── ağ oynatma saati ─────────────────────────────── */

/** Oynatma denetleyicisinin tek durumu: hatanın yavaş ortalaması. */
export interface NetClockState { err: number }
export const createNetClock = (): NetClockState => ({ err: 0 });

/**
 * Ağ maçında yerel sim saatinin hız çarpanı (1'e çok yakın).
 *
 * Sim adımları paket VARIŞINDAN sürülemez: sunucu yayını düzgün olsa bile paketler yolda öbekleniyor
 * (ölçüm T.NET yorumunda) ve bu doğrudan takılma olarak görülüyor. Bunun yerine sim yerel saatle akıyor;
 * burası da o saati sunucununkine kilitliyor.
 *
 * Ölçü: dünya tick'i, sunucudan gelen SON tick'in `lead + leadTicks` kadar ilerisinde dursun. Ham hata öbek başına
 * ±6 zıpladığı için düzeltme YAVAŞ ortalama üstünden yapılıyor ve ±%6 ile kırpılıyor — oyuncu hız değişimini
 * fark etmemeli, yalnız uzun vadeli kayma kapanmalı.
 */
export function netPlayoutRate(s: NetClockState, serverTick: number, worldTick: number, lead: number): number {
  // `lead` DIŞARIDAN geliyor: yerel-önce oynatmada hedef, kendi girdimizin yazıldığı tick (istemci hesaplar,
  // `ahead` ile değişir). `T.NET.leadTicks` onun üstüne eklenen ince ayar.
  s.err += (serverTick + lead + T.NET.leadTicks - worldTick - s.err) * T.NET.clockEma;
  const band = T.NET.deadbandTicks;
  // Ölü bant içinde hız TAM 1 — sim mükemmel düzgün akar. Bandı ÇIKARARAK düzeltiyoruz ki kenarda
  // sıçrama olmasın (bant dışına çıkınca hız 1'den sürekli olarak ayrılsın).
  const e = s.err > band ? s.err - band : s.err < -band ? s.err + band : 0;
  if (e === 0) return 1;
  const r = 1 + e * T.NET.clockGain;
  return r < T.NET.rateMin ? T.NET.rateMin : r > T.NET.rateMax ? T.NET.rateMax : r;
}

/** Render alfa'sı: interpolasyon açık ya da slow-mo (kural 3) → acc/TICK_DT (≤ 1); aksi hâlde 1 (en son sim durumu çizilir). */
export function renderAlpha(clock: FrameClock, timeScale: number): number {
  return clock.interpolate || timeScale < 1 ? Math.min(1, clock.acc / TICK_DT) : 1;
}

/** rAF aralığı örneği (ms; tavanlı, snap'siz). RATE_SKIP + RATE_SAMPLES dolunca medyan kararı: snap60 / interpolate / rafMedianMs.
 *  Döner: karar bu çağrıda verildi mi (game.ts istatistiği o zaman günceller). Ölçüm dışında no-op. */
export function sampleRate(clock: FrameClock, ms: number): boolean {
  if (!clock.measuring) return false;
  clock.samples.push(ms);
  if (clock.samples.length < RATE_SKIP + RATE_SAMPLES) return false;
  const s = clock.samples.slice(RATE_SKIP).sort((a, b) => a - b);
  const med = s[s.length >> 1] ?? RAF_60_MS;
  clock.rafMedianMs = med;
  clock.snap60 = Math.abs(med - RAF_60_MS) <= RATE_TOL;
  clock.interpolate = !clock.snap60;
  clock.measuring = false;
  clock.samples.length = 0;
  return true;
}

/** resize / görünürlük: yeniden ölç. Önceki karar (snap60 / interpolate) ölçüm bitene kadar KORUNUR — 120/144 Hz'de her pencere
 *  boyutlandırmasında 98 kare interpolasyon kapanıp kamera takılmasın (bulgu 2). İlk ölçüm için varsayılan zaten 60 Hz kabulü. */
export function restartRateMeasure(clock: FrameClock): void {
  clock.samples.length = 0;
  clock.measuring = true;
}
